/*
  <ilura-reveal> — the scroll reveal used by every ILURA section.

  A reveal is a nicety and never a gate on content, so the hidden state is
  opt-in by this element rather than the default:

  - With no JavaScript, or before this file parses, the element carries no
    data-in attribute at all and component-ilura-base.css leaves it visible.
    Nothing here can hide content it then fails to show.
  - An element already in view on connect is shown immediately, with no
    transition, so above-the-fold copy never fades in on a reload.
  - If the observer never reports — a hidden iframe, print, a programmatic
    scroll, thumbnail capture — a timer opens it anyway.

  A custom element rather than a DOMContentLoaded pass because
  connectedCallback re-fires when the theme editor re-renders a section, and
  because disconnectedCallback gives somewhere to drop the observer and the
  timer. A DOMContentLoaded listener would leave every editor edit observing a
  detached node.

  Attributes:
    delay      ms added to the transition, for staggering a row. Default 0.
    threshold  fraction of the element that must be visible. Default 0.12;
               figures use a higher value so they land as a set.
*/
if (!customElements.get('ilura-reveal')) {
  class IluraReveal extends HTMLElement {
    connectedCallback() {
      /* Already revealed by a previous connect (theme editor re-render), so
         don't hide it again and replay the animation. */
      if (this.dataset.in === 'true') return;

      const delay = parseInt(this.getAttribute('delay'), 10);
      if (delay > 0) this.style.transitionDelay = `${delay}ms`;

      const box = this.getBoundingClientRect();
      const inView = box.top < (window.innerHeight || 0) && box.bottom > 0;
      if (inView) {
        this.dataset.in = 'true';
        return;
      }

      this.dataset.in = 'false';

      if (!('IntersectionObserver' in window)) {
        this.dataset.in = 'true';
        return;
      }

      const threshold = parseFloat(this.getAttribute('threshold')) || 0.12;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) this.reveal();
        },
        { threshold }
      );
      this.observer.observe(this);

      this.failsafe = setTimeout(() => this.reveal(), 2500);
    }

    disconnectedCallback() {
      this.teardown();
    }

    reveal() {
      this.dataset.in = 'true';
      this.teardown();
    }

    teardown() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.failsafe) {
        clearTimeout(this.failsafe);
        this.failsafe = null;
      }
    }
  }

  customElements.define('ilura-reveal', IluraReveal);
}
